import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { FileCheck, Download, Car, Calendar, User, Euro, XCircle, ChevronDown, ChevronRight, Banknote, CheckCircle } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrdersPage = () => {
  const { getAuthHeader, user, getImpersonateParams, impersonatedUser } = useAuth();
  const [orders, setOrders] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedMonths, setExpandedMonths] = useState({});

  const isAdmin = ['admin', 'super_admin', 'top_agent'].includes(user?.role);

  const fetchOrders = useCallback(async () => {
    try {
      // Passa view_as se Admin sta impersonando un utente
      const impersonateParams = getImpersonateParams();
      const queryParams = new URLSearchParams(impersonateParams);
      const queryString = queryParams.toString();
      const url = queryString ? `${API}/orders?${queryString}` : `${API}/orders`;
      
      const response = await axios.get(url, { headers: getAuthHeader() });
      setOrders(response.data);
      // Expand all months by default
      const expanded = {};
      Object.keys(response.data).forEach(month => {
        expanded[month] = true;
      });
      setExpandedMonths(expanded);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Errore nel caricamento degli ordini');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, getImpersonateParams]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleDownloadPdf = async (orderId) => {
    try {
      const response = await axios.get(`${API}/offers/${orderId}/pdf`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contratto_${orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF scaricato');
    } catch (error) {
      toast.error('Errore nel download del PDF');
    }
  };

  const handleInvalidate = async (orderId) => {
    if (!window.confirm('Sei sicuro di voler riportare questo contratto a offerta?')) return;
    
    try {
      await axios.post(`${API}/offers/${orderId}/invalidate`, {}, { headers: getAuthHeader() });
      toast.success('Contratto riportato a offerta');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'annullamento');
    }
  };

  const handleTogglePaid = async (orderId, currentPaidStatus) => {
    try {
      if (currentPaidStatus) {
        await axios.post(`${API}/offers/${orderId}/unpay`, {}, { headers: getAuthHeader() });
        toast.success('Pagamento annullato');
      } else {
        await axios.post(`${API}/offers/${orderId}/pay`, {}, { headers: getAuthHeader() });
        toast.success('Contratto segnato come pagato');
      }
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'operazione');
    }
  };

  const toggleMonth = (month) => {
    setExpandedMonths(prev => ({
      ...prev,
      [month]: !prev[month]
    }));
  };

  const formatMonth = (monthStr) => {
    if (!monthStr || monthStr === 'N/A') return 'Senza data';
    try {
      const [year, month] = monthStr.split('-');
      const date = new Date(year, parseInt(month) - 1);
      return date.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
    } catch {
      return monthStr;
    }
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('it-IT', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Calculate totals per month
  const calculateMonthTotals = (monthOrders) => {
    return monthOrders.reduce((acc, order) => ({
      canone: acc.canone + (order.canone_finale || 0),
      provvigioni: acc.provvigioni + (order.provvigione_agente || 0),
      provvigioniPagate: acc.provvigioniPagate + (order.pagato ? (order.provvigione_agente || 0) : 0),
      count: acc.count + 1,
      countPagati: acc.countPagati + (order.pagato ? 1 : 0)
    }), { canone: 0, provvigioni: 0, provvigioniPagate: 0, count: 0, countPagati: 0 });
  };

  // Sort months in descending order
  const sortedMonths = Object.keys(orders).sort((a, b) => {
    if (a === 'N/A') return 1;
    if (b === 'N/A') return -1;
    return b.localeCompare(a);
  });

  return (
    <div className="space-y-6" data-testid="orders-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans' }}>
          Ordini (Contratti)
        </h1>
        <p className="text-slate-500 mt-1">Contratti validati divisi per mese di competenza</p>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-slate-100 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : sortedMonths.length > 0 ? (
        <div className="space-y-4">
          {sortedMonths.map(month => {
            const monthOrders = orders[month];
            const totals = calculateMonthTotals(monthOrders);
            const isExpanded = expandedMonths[month];
            
            return (
              <Card key={month} className="overflow-hidden">
                <Collapsible open={isExpanded} onOpenChange={() => toggleMonth(month)}>
                  <CollapsibleTrigger className="w-full">
                    <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-3 text-lg">
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-slate-400" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          )}
                          <Calendar className="w-5 h-5 text-primary" />
                          <span className="capitalize">{formatMonth(month)}</span>
                          <Badge variant="secondary" className="ml-2">
                            {totals.count} contratt{totals.count === 1 ? 'o' : 'i'}
                          </Badge>
                          {totals.countPagati > 0 && (
                            <Badge className="bg-green-100 text-green-700 ml-1">
                              {totals.countPagati} pagat{totals.countPagati === 1 ? 'o' : 'i'}
                            </Badge>
                          )}
                        </CardTitle>
                        <div className="flex items-center gap-6 text-sm">
                          <div className="text-right">
                            <p className="text-slate-500">Provvigioni</p>
                            <p className="font-bold text-emerald-600 text-lg">
                              €{Math.round(totals.provvigioni).toLocaleString('it-IT')}
                            </p>
                            {totals.provvigioniPagate > 0 && (
                              <p className="text-xs text-green-600">
                                €{Math.round(totals.provvigioniPagate).toLocaleString('it-IT')} pagate
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Veicolo</TableHead>
                              <TableHead>Cliente</TableHead>
                              <TableHead>Agente</TableHead>
                              <TableHead className="text-right">Canone</TableHead>
                              <TableHead className="text-right">Provvigione</TableHead>
                              <TableHead>Stato</TableHead>
                              <TableHead className="text-right">Azioni</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {monthOrders.map(order => (
                              <TableRow key={order.id} data-testid={`order-row-${order.id}`} className={order.pagato ? 'bg-green-50' : ''}>
                                <TableCell>
                                  <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${order.pagato ? 'bg-green-100' : 'bg-emerald-100'}`}>
                                      <Car className={`w-5 h-5 ${order.pagato ? 'text-green-600' : 'text-emerald-600'}`} />
                                    </div>
                                    <div>
                                      <p className="font-medium text-slate-800">
                                        {order.vehicle_info?.marca} {order.vehicle_info?.modello}
                                      </p>
                                      <p className="text-sm text-slate-500">
                                        {order.durata_mesi} mesi • {order.km_annui?.toLocaleString('it-IT')} km
                                      </p>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {order.contact_info?.nome_cognome ? (
                                    <div className="flex items-center gap-2">
                                      <User className="w-4 h-4 text-slate-400" />
                                      <span className="text-slate-700">{order.contact_info.nome_cognome}</span>
                                    </div>
                                  ) : (
                                    <span className="text-slate-400">-</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <span className="text-slate-700 font-medium">{order.user_name}</span>
                                </TableCell>
                                <TableCell className="text-right">
                                  <p className="font-semibold text-slate-800">
                                    €{order.canone_finale?.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                                  </p>
                                </TableCell>
                                <TableCell className="text-right">
                                  <p className={`font-semibold flex items-center justify-end gap-1 ${order.pagato ? 'text-green-600' : 'text-emerald-600'}`}>
                                    <Euro className="w-3 h-3" />
                                    {Math.round(order.provvigione_agente || 0).toLocaleString('it-IT')}
                                  </p>
                                </TableCell>
                                <TableCell>
                                  {order.pagato ? (
                                    <Badge className="bg-green-100 text-green-700">
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                      Pagato
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-amber-600 border-amber-300">
                                      Da pagare
                                    </Badge>
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={() => handleDownloadPdf(order.id)}
                                      data-testid={`download-pdf-${order.id}`}
                                    >
                                      <Download className="w-4 h-4" />
                                    </Button>
                                    {isAdmin && (
                                      <>
                                        <Badge className="bg-emerald-100 text-emerald-700">
                                          <CheckCircle className="w-3 h-3 mr-1" />
                                          Contratto Attivo
                                        </Badge>
                                        <Button 
                                          size="sm"
                                          variant={order.pagato ? "outline" : "default"}
                                          className={order.pagato ? "text-amber-600 hover:text-amber-700" : "bg-green-600 hover:bg-green-700"}
                                          onClick={() => handleTogglePaid(order.id, order.pagato)}
                                          data-testid={`toggle-paid-${order.id}`}
                                        >
                                          <Banknote className="w-4 h-4 mr-1" />
                                          {order.pagato ? 'Annulla' : 'Pagato'}
                                        </Button>
                                        <Button 
                                          variant="outline"
                                          size="sm"
                                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                          onClick={() => handleInvalidate(order.id)}
                                          data-testid={`invalidate-${order.id}`}
                                        >
                                          <XCircle className="w-4 h-4" />
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <FileCheck className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-800">Nessun ordine</h3>
              <p className="text-slate-500 mt-1">I contratti validati appariranno qui</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default OrdersPage;
